import { useNavigate, useParams } from "react-router";
import { connect, useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import classNames from "classnames";
import { State } from "redux/store";
import { useEffect, useRef, useState } from "react";
import {
  CLUSTER_DETAILS_PAGE,
  DATA_PAGE,
  DATASET_CREATION_PAGE,
  IMAGE_DETAILS_PAGE,
} from "helpers/constants/pages";
import {
  getDataset,
  getExportCount,
  getExportDataArray,
  getFilteredSimilarityClusters,
  getIsDataIngestionTabEnabled,
  getIsTrainWorthy,
  getNavigationCluster,
  getShowCart,
  getShowExportModal,
  isSharingEnabled,
  getIsUserManagementEnabled,
  getExportStatus,
} from "redux/SingleDataset/selectors";
import { getUserInfo, getIsUserConfigLoaded } from "redux/User/selectors";
import { getImageDataLoader } from "redux/Modals/selectors";
import ProgressBarLoader from "views/uikit/ProgressBarLoader";
import {
  getCreationStatus,
  getIsLoading,
  getIsNewDatasetReady,
  getNewDataset,
  getNewDatasetStatus,
} from "redux/CreateDataset/selectors";

import { setShowDeleteDatasetModal } from "redux/CreateDataset/actions";
import {
  setShowCart,
  setShowExportModal,
  setShowSharingMenu,
} from "redux/SingleDataset/actions";
import { copyToClipboard, isOnPremUser } from "helpers/utility/utilities";
import { isAnonymousUser, userLogout } from "redux/authReducer";
import DatasetDeletionModal from "views/modals/DatasetDeletionModal";
import EditableDatasetTitle from "../EditableDatasetTitle";
import TooltipWrapper from "views/uikit/TooltipWrapper";
import DeprecatedDatasetStatusComponent from "./DatasetStatus";
import ExportModal from "views/modals/ExportModal";
import ActionOptions from "./ActionOptions";
import TabSwitch from "./TabSwitch";
import { PRIVATE_DATASETS_PATH } from "helpers/constants/paths";
import {
  DatasetStatusEnum,
  EDatasetStatus,
  ExportModalSource,
  TabTypes,
} from "types";
import CheckIcon from "assets/icons/CheckIcon";
import KebabIcon from "assets/icons/KebabIcon";
import ShareIcon from "assets/icons/ShareIcon";
import styles from "./style.module.scss";
import { getVisibleSubtasks } from "views/pages/DatasetCreationPage/DatasetCreationWizard/Steps/CreateDataset";
import { DatasetInfoIcon } from "../../pages/DatasetInventory/DatasetInfoIcon/DatasetInfoIcon";
import { TrainModel } from "./TrainModel/TrainModel";
import ReloadIcon from "assets/icons/ReloadIcon";
import ReindexModal from "./ReindexModal";
import ContainerHeaderLayout from "../ContainerHeaderLayout/ContainerHeaderLayout";
import ExportIcon from "../../../assets/icons/ExportIcon";
import { useLabelPropagationExport } from "hooks/LabelPropagation/useLabelPropagationExport";
import { useEvaluateLabelPropagationModal } from "hooks/LabelPropagation/useEvaluateLabelPropagationModal";
import EvaluateLabelPropagationModal from "views/modals/EvaluateLabelPropagationModal";
import VLPopover from "../../uikit/VLPopover";
import { useLocation } from "react-router-dom";
import { getIsAddMediaEnabled } from "redux/Datasets/selectors";
import { DatasetSnapshotsButton } from "./DatasetSnapshots/DatasetSnapshotsButton";
import { Tooltip } from "antd";
import { DatasetShareButton } from "./DatasetShareOption/DatasetShareButton";
import { ShoppingCartIcon } from "../../../assets/icons/ShoppingCartIcon";
import {
  GuideBook,
  GuideButton,
  useAutoOpenGuide,
} from "views/components/Guide";
import {
  useAddMediaGuideStore,
  useCreateDatasetGuideStore,
  useExplorationGuideStore,
} from "views/components/Guide/guides/guideStores";
import { EXPLORATION_TOPICS } from "views/components/Guide/guides/explorationGuide";
import DatasetStatus from "./DatasetStatus/DatasetStatus";
import {
  ADD_MEDIA_TAB,
  DATASET_SNAPSHOTS,
  DELETE_DATASET,
  NEW_STATUS,
  TRAIN_MODEL,
} from "../../../helpers/constants/filters";
import { useFeatureFlagService } from "hooks/useFeatureFlagService";
import { useUserPermission } from "../../../hooks/useUserPermission";
import { LoadingOutlined } from "@ant-design/icons";

const ShowLoader = ({
  dynamicPage,
  isImageLoading,
  isCardListLoading,
}: any) => {
  const isImageDetailsPage = dynamicPage === IMAGE_DETAILS_PAGE;
  const isClusterOrDataPage =
    dynamicPage === CLUSTER_DETAILS_PAGE || dynamicPage === DATA_PAGE;
  const isDatasetCrationPage = dynamicPage === DATASET_CREATION_PAGE;

  if (isImageDetailsPage) {
    return <ProgressBarLoader loading={isImageLoading} />;
  }

  if (isClusterOrDataPage || isDatasetCrationPage) {
    return <ProgressBarLoader loading={isCardListLoading} />;
  }

  return null;
};

const ContainerHeader = ({
  page = DATA_PAGE,
  dynamicPage = DATA_PAGE,
  isContentLoading = false,
  isCardListLoading = false,
  dataset,
  exportCount,
  showCart,
  isImageLoading,
  isCreateDatasetLoading,
  isDataIngestionTabEnabled,
  isNewDatasetReady,
  newDataset,
  creationStatus,
  userId,
  navigationClusterLoader = false,
  showExportModal,
  sharingEnabled,
  isTrainWorthy,
  title,
  className,
}: any) => {
  const isUserManagementEnabled = useSelector(getIsUserManagementEnabled);
  const displayDataIngestionTab = useFeatureFlagService(ADD_MEDIA_TAB).show;

  const location = useLocation();
  const isDatasetSnapshotEnabled =
    useFeatureFlagService(DATASET_SNAPSHOTS).show;
  const [showOptionsDropdown, setShowOptionsDropdown] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const isDatasetConfigLoaded = useSelector(getIsUserConfigLoaded);
  const [datasetStatus, setDatasetStatus] = useState<DatasetStatusEnum | null>(
    null,
  );
  const displayNewStatus = useFeatureFlagService(NEW_STATUS).show;
  const isAddMediaEnabled = useSelector(getIsAddMediaEnabled);
  const canDeleteDataset = useUserPermission("can_delete");
  const isDeleteDatasetEnabled =
    useFeatureFlagService(DELETE_DATASET).enabled && canDeleteDataset;
  const deprecatedDisplayReindex =
    datasetStatus === DatasetStatusEnum.PENDING_INDEX && isAddMediaEnabled;
  const exportButtonRef = useRef<HTMLDivElement>(null);
  const optionsBtnRef = useRef<HTMLDivElement>(null);

  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { newDatasetId, enrichDatasetId, datasetId, viewsDatasetId } =
    useParams();

  const { isExportLabelPropagation: isEvaluateLabelPropation } =
    useLabelPropagationExport(datasetId);

  const allowUpdateDataset =
    useSelector(getIsAddMediaEnabled) &&
    dataset?.status !== DatasetStatusEnum.INDEXING &&
    dataset?.status !== DatasetStatusEnum.SAVING;
  const {
    isOpen: isOpenEvaluateLabelPropagationModal,
    closeModal: handleCloseModalEvaluateLabelPropagation,
    openModal: handleEvaluateLabelPropagationOpenModal,
    handleRunEvaluation,
    form,
  } = useEvaluateLabelPropagationModal(datasetId);

  const trainModelBehavior = useFeatureFlagService(TRAIN_MODEL);

  const isDataCreationPage = dynamicPage === DATASET_CREATION_PAGE;
  const dynamicIsCardListLoading = isDataCreationPage
    ? isCreateDatasetLoading
    : dynamicPage === DATA_PAGE
      ? isCardListLoading
      : navigationClusterLoader;
  const dynamicDataset = newDataset || dataset;
  const explorationGuideOpen = useExplorationGuideStore((s) => s.open);
  const openExplorationGuide = useExplorationGuideStore((s) => s.openGuide);
  const closeExplorationGuide = useExplorationGuideStore((s) => s.closeGuide);
  useAutoOpenGuide(
    useExplorationGuideStore,
    !isDataCreationPage && !!dynamicDataset,
  );
  const openCreateGuide = useCreateDatasetGuideStore((s) => s.openGuide);
  const openAddMediaGuide = useAddMediaGuideStore((s) => s.openGuide);
  const isAddMediaWizard = window.location.pathname.includes("/update");
  const canReindex = useUserPermission("can_reindex");
  const displayReindex =
    displayNewStatus &&
    dynamicDataset?.status_new === EDatasetStatus.PARTIAL_INDEX &&
    isAddMediaEnabled &&
    canReindex;
  const [isReindexModalOpen, setIsReindexModalOpen] = useState(
    displayReindex || deprecatedDisplayReindex,
  );

  const isUpdateMode = location.pathname.includes("/update");
  const showDynamicTabs = (): TabTypes[] => {
    let result: TabTypes[] = [];
    //only show tabs after datasetStatus has loaded
    if (datasetStatus) {
      if (displayNewStatus) {
        if (!newDataset?.creating) {
          result.push(TabTypes.DATA_EXPLORATION);
        }
        if (displayDataIngestionTab) {
          result.push(TabTypes.DATA_INGESTION);
        }
        return result;
      }

      let isReady = [
        DatasetStatusEnum.READY,
        DatasetStatusEnum.ENRICHING,
      ].includes(datasetStatus as any);

      const displayDataTabForUpdatedDataset =
        allowUpdateDataset && isUpdateMode;
      if (isReady || datasetId || displayDataTabForUpdatedDataset) {
        result.push(TabTypes.DATA_EXPLORATION);
        if (allowUpdateDataset) {
          result.push(TabTypes.DATA_INGESTION);
        }
      }
      if (isDataIngestionTabEnabled || newDatasetId) {
        result.push(TabTypes.DATA_INGESTION);
      }
    }
    return result;
  };
  const isPublicDataset = dynamicDataset?.sample === "DEFAULT_SAMPLE";

  const showPublicHeader = isPublicDataset && !isContentLoading;
  const showCartButton = !isDataCreationPage;
  const showDescription = !showPublicHeader && !!dynamicDataset;
  const showDatasetStatus =
    !showPublicHeader && !!datasetStatus && !isDataCreationPage;
  const isCreator = !newDataset?.created_by || newDataset.created_by === userId;
  const isDatasetProcessing = displayNewStatus
    ? (creationStatus?.status_new ?? newDataset?.status_new) ===
      EDatasetStatus.INDEXING
    : [
        DatasetStatusEnum.INITIALIZING,
        DatasetStatusEnum.PRE_PROCESSING,
        DatasetStatusEnum.INDEXING,
        DatasetStatusEnum.SAVING,
      ].includes(creationStatus?.status ?? newDataset?.status);
  const showAbortButton =
    isDataCreationPage &&
    !showPublicHeader &&
    !isNewDatasetReady &&
    !isUpdateMode &&
    !newDataset?.parent_dataset_id &&
    isCreator &&
    isDatasetProcessing;

  const canExport = useUserPermission("can_export");
  const canViewShareList = useUserPermission("can_view");

  const showExportButton = !newDatasetId && canExport;
  const handleExportClick = () => {
    dispatch(
      setShowExportModal(
        !showExportModal ? ExportModalSource.CONTAINER_HEADER : null,
      ),
    );
  };

  const containerHeaderDetailClassnames = classNames(
    styles.containerHeaderDetails,
    {
      [styles.justifySpaceBetween]: isPublicDataset,
    },
  );

  const optionsButtonClassnames = classNames(styles.optionsButton, {
    [styles.selectedOptionsButton]: showOptionsDropdown,
  });

  const toggleOptionsDropdown = () => {
    setShowOptionsDropdown(!showOptionsDropdown);
  };

  const handleAbortClick = () => {
    dispatch(setShowDeleteDatasetModal(true));
  };

  const handleShareBtnClick = (event: React.MouseEvent<HTMLDivElement>) => {
    if (isAnonymousUser()) {
      return;
    }
    event.stopPropagation();
    dispatch(setShowSharingMenu(true));
  };

  const handleSignInClick = (event: React.MouseEvent<HTMLDivElement>) => {
    event.stopPropagation();
    userLogout();
  };

  const handleDeleteOptionClick = () => {
    setShowDeleteModal(true);
  };

  const handleNavigattionOnDelete = () => {
    navigate(PRIVATE_DATASETS_PATH);
  };

  const handleCopyURLClick = () => {
    let urlToCopy = new URL(window.location.href);
    copyToClipboard(urlToCopy.toString());

    toast(
      <div className={styles.toastNotification}>
        <CheckIcon />
        <span>Copied to clipboard!</span>
      </div>,

      {
        position: "bottom-center",
        closeButton: false,
      },
    );
  };

  const renderEditableTitle = () => {
    const isCreator = dynamicDataset?.created_by === userId;
    const isEditable = isOnPremUser() || isCreator;

    return isEditable ? (
      <EditableDatasetTitle
        title={dynamicDataset?.display_name || ""}
        datasetID={dynamicDataset?.id || ""}
        isContentLoading={isContentLoading}
        fontStyle={{ width: "310px" }}
      />
    ) : (
      <span
        className={styles.containerHeaderTitleText}
        title={dynamicDataset?.display_name}
      >
        {dynamicDataset?.display_name}
      </span>
    );
  };

  const canSendToTraining = useUserPermission("can_send_to_training");

  const showModelJobs =
    trainModelBehavior.show && page !== DATASET_CREATION_PAGE;

  const renderTrainModelBtn = () => {
    if (!showModelJobs || !dataset?.id) {
      return null;
    }
    return (
      <TrainModel
        isTrainWorthy={isTrainWorthy}
        dataset={dataset}
        canManageJobs={!!canSendToTraining}
      />
    );
  };

  const renderSnapshotBtn = () => {
    if (!isDatasetSnapshotEnabled || page === DATASET_CREATION_PAGE) {
      return null;
    }
    return (
      <DatasetSnapshotsButton
        className={styles.actionButton}
        datasetId={datasetId!}
      />
    );
  };
  const renderShoppingCartBtn = () => {
    if (!showCartButton) {
      return null;
    }

    if (exportCount > 0) {
      return (
        <Tooltip title="Selected items">
          <div
            onClick={() => dispatch(setShowCart(!showCart))}
            className={styles.actionButton}
          >
            <ShoppingCartIcon />
            <div className={styles.cartCount}>
              {exportCount.toLocaleString()}
            </div>
          </div>
        </Tooltip>
      );
    }
    return null;
  };

  const renderShareBtn = () => {
    if (!sharingEnabled || newDatasetId || !canViewShareList) {
      return <noscript />;
    }
    const shareBtn = (
      <Tooltip title="Share">
        <div onClick={handleShareBtnClick} className={styles.actionButton}>
          <ShareIcon />
        </div>
      </Tooltip>
    );
    if (isOnPremUser() && isUserManagementEnabled) {
      return (
        <DatasetShareButton
          className={styles.actionButton}
          datasetId={datasetId!}
        />
      );
    } else if (!isAnonymousUser()) {
      return shareBtn;
    } else {
      return (
        <div data-tooltip-id="share-btn-tooltip" data-tooltip-place="bottom">
          {shareBtn}
          <TooltipWrapper
            id="share-btn-tooltip"
            isClickable={true}
            customClass="shareTooltip"
          >
            <div className={styles.shareTooltip}>
              <div className={styles.tooltipText}>
                Only signed-in users can share public Datasets. Please sign-up
                or sign-in to proceed
              </div>
              <div className={styles.signInBtn} onClick={handleSignInClick}>
                Sign In/Sign Up
              </div>
            </div>
          </TooltipWrapper>
        </div>
      );
    }
  };

  const exportStatus = useSelector(getExportStatus);
  const isExportInProgress = exportStatus === "IN_PROGRESS";

  const renderExportBtn = () => {
    if (!showExportButton) {
      return <noscript />;
    }

    return (
      <Tooltip title={isExportInProgress ? "Export in progress" : "Export"}>
        <div
          ref={exportButtonRef}
          onClick={isExportInProgress ? undefined : handleExportClick}
          className={styles.actionButton}
        >
          {isExportInProgress ? <LoadingOutlined /> : <ExportIcon />}
        </div>
      </Tooltip>
    );
  };

  const renderOptionsBtn = () => {
    if (newDatasetId || !dynamicDataset) {
      return <noscript />;
    }

    return (
      <Tooltip
        title={!showOptionsDropdown ? "More Actions" : ""}
        placement="bottomRight"
      >
        <div
          ref={optionsBtnRef}
          onClick={toggleOptionsDropdown}
          className={optionsButtonClassnames}
        >
          <KebabIcon />

          {showOptionsDropdown && (
            <div className={styles.actionOptionsPositioner}>
              <ActionOptions
                callerRef={optionsBtnRef}
                show={showOptionsDropdown}
                onToggle={toggleOptionsDropdown}
                onCopyClick={handleCopyURLClick}
                showDeleteOption={isDeleteDatasetEnabled && !isAnonymousUser()}
                onDeleteClick={handleDeleteOptionClick}
                showExportOption={false}
                onExportClick={handleExportClick}
                showEvaluateLabelPropationOption={isEvaluateLabelPropation}
                onEvaluateLabelPropagationClick={
                  handleEvaluateLabelPropagationOpenModal
                }
              />
            </div>
          )}

          <div className={styles.exportModalPositioner}>
            <ExportModal
              showModal={showExportModal === ExportModalSource.CONTAINER_HEADER}
              openedFrom={ExportModalSource.CONTAINER_HEADER}
              callerButtonRef={exportButtonRef}
            />
          </div>

          <DatasetDeletionModal
            datasetId={dataset?.id}
            showModal={showDeleteModal}
            setShowModal={setShowDeleteModal}
            datasetName={dataset?.display_name}
            datasetSource={dataset?.source_type}
            handleNavigate={handleNavigattionOnDelete}
          />
        </div>
      </Tooltip>
    );
  };

  useEffect(() => {
    let status: DatasetStatusEnum | null = null;

    if (datasetId || enrichDatasetId || viewsDatasetId) {
      status = dataset?.status || null;
    } else if (newDatasetId) {
      status = (newDataset || dataset)?.status || null;
    }
    if (creationStatus?.secondaryStatus?.length) {
      const visible = getVisibleSubtasks(creationStatus.secondaryStatus);
      const lastInProgressIndex = visible.findIndex((s) => s.progress < 100);
      if (lastInProgressIndex !== -1) {
        status = visible[lastInProgressIndex].status as DatasetStatusEnum;
      } else {
        status = creationStatus.status;
      }
    }
    setDatasetStatus(status);
  }, [
    datasetId,
    enrichDatasetId,
    newDatasetId,
    viewsDatasetId,
    newDataset,
    dataset,
    creationStatus,
  ]);

  const renderEvaluateLabelPropagation = () => {
    return (
      <EvaluateLabelPropagationModal
        isOpen={isOpenEvaluateLabelPropagationModal}
        onClose={handleCloseModalEvaluateLabelPropagation}
        onRunEvaluation={handleRunEvaluation}
        form={form}
      />
    );
  };

  return (
    <ContainerHeaderLayout
      className={className}
      upperRow={
        <>
          <div className={styles.containerLeft}>
            <div className={containerHeaderDetailClassnames}>
              {showDescription && (
                <DatasetInfoIcon dataset={dynamicDataset} showDatasetInfo />
              )}
              <div className={styles.containerHeaderTitleContainer}>
                {title || renderEditableTitle()}
              </div>
            </div>
          </div>
          {isDatasetConfigLoaded ? (
            <>
              <TabSwitch
                showTabs={showDynamicTabs()}
                dataset={dynamicDataset}
                className={styles.tabs}
              />
              <div className={styles.filler} />
              {dynamicDataset?.status_new &&
                displayNewStatus &&
                !isDataCreationPage && (
                  <DatasetStatus
                    status={dynamicDataset.status_new}
                    datasetId={dynamicDataset.id}
                    icon={displayReindex ? <ReloadIcon /> : undefined}
                    onClick={
                      displayReindex
                        ? () => setIsReindexModalOpen(true)
                        : undefined
                    }
                  />
                )}
              {showDatasetStatus && dynamicDataset?.id && !displayNewStatus && (
                <DeprecatedDatasetStatusComponent
                  datasetStatus={datasetStatus}
                  icon={deprecatedDisplayReindex ? <ReloadIcon /> : undefined}
                  onClick={
                    deprecatedDisplayReindex
                      ? () => setIsReindexModalOpen(true)
                      : undefined
                  }
                  datasetId={dynamicDataset.id}
                />
              )}
              {showPublicHeader && (
                <VLPopover title="This is public read-only sample dataset">
                  <div className={styles.publicLabel}>Public</div>
                </VLPopover>
              )}
              <div className={styles.actionsDivider}></div>
              <div className={styles.actionButtons}>
                {showAbortButton && (
                  <div
                    className={styles.abortButton}
                    onClick={handleAbortClick}
                  >
                    Abort
                  </div>
                )}
                {!isDataCreationPage && dynamicDataset && (
                  <GuideButton
                    tooltip="How to explore this dataset"
                    onClick={openExplorationGuide}
                  />
                )}
                {isDataCreationPage && (
                  <GuideButton
                    tooltip={
                      isAddMediaWizard
                        ? "How adding media works"
                        : "How creating a dataset works"
                    }
                    onClick={
                      isAddMediaWizard ? openAddMediaGuide : openCreateGuide
                    }
                  />
                )}
                <GuideBook
                  open={explorationGuideOpen}
                  onClose={closeExplorationGuide}
                  heading="Exploring a dataset"
                  topics={EXPLORATION_TOPICS}
                  hint="Reopen this guide any time from the ? in the dataset header."
                />
                {renderTrainModelBtn()}
                <div className={styles.buttonGroup}>
                  {renderShoppingCartBtn()}
                  {renderSnapshotBtn()}
                  {renderShareBtn()}
                  {renderExportBtn()}
                  {renderOptionsBtn()}
                  {renderEvaluateLabelPropagation()}
                </div>
              </div>
              <ReindexModal
                open={isReindexModalOpen}
                onClose={() => setIsReindexModalOpen(false)}
              />
            </>
          ) : null}
        </>
      }
      lowerRow={
        <>
          <ShowLoader
            dynamicPage={dynamicPage}
            isImageLoading={isImageLoading}
            isCardListLoading={dynamicIsCardListLoading}
          />
        </>
      }
    />
  );
};

const mapStateToProps = (state: State) => {
  return {
    dataset: getDataset(state),
    exportCount: getExportCount(state),
    exportData: getExportDataArray(state),
    showCart: getShowCart(state),
    clusters: getFilteredSimilarityClusters(state),
    navigationCluster: getNavigationCluster(state),
    isImageLoading: getImageDataLoader(state),
    isDataIngestionTabEnabled: getIsDataIngestionTabEnabled(state),
    isCreateDatasetLoading: getIsLoading(state),
    newDatasetStatus: getNewDatasetStatus(state),
    creationStatus: getCreationStatus(state),
    isNewDatasetReady: getIsNewDatasetReady(state),
    newDataset: getNewDataset(state),
    userId: getUserInfo(state).userId,
    showExportModal: getShowExportModal(state),
    sharingEnabled: isSharingEnabled(state),
    isTrainWorthy: getIsTrainWorthy(state),
  };
};

export default connect(mapStateToProps)(ContainerHeader);
